import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  GitPullRequest,
  GitMerge,
  Sparkles,
  CheckCircle2,
  XCircle,
  Clock,
  User,
  Users,
  Calendar,
  Wallet,
  MapPin,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Check,
  Split,
  Layers,
  ArrowRight,
  ShieldCheck,
  Compass,
  FileText,
  Loader2,
  Sliders,
  Utensils,
  SunMedium,
  Building,
  Car,
  Activity,
  Trash2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import tripsApi from '../api/tripsApi';
import Loader from './Loader';

export default function TripChangeRequestsView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [trip, setTrip] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selected change request IDs for batch merging
  const [selectedIds, setSelectedIds] = useState([]);

  // Expanded diff view per request ID
  const [expandedDiffs, setExpandedDiffs] = useState({});

  // Single Merge & Reject States
  const [singleMergeLoading, setSingleMergeLoading] = useState({});
  const [rejectModalState, setRejectModalState] = useState({
    isOpen: false,
    requestId: null,
    reason: '',
    isSubmitting: false,
  });

  // AI Merge States
  const [isAiMerging, setIsAiMerging] = useState(false);
  const [aiMergeResult, setAiMergeResult] = useState(null);
  const [showAiModal, setShowAiModal] = useState(false);
  const [isApplyingAi, setIsApplyingAi] = useState(false);

  // Manual Merge States
  const [isManualMerging, setIsManualMerging] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualPlanDraft, setManualPlanDraft] = useState(null);

  // Notification / Feedback banner
  const [actionSuccess, setActionSuccess] = useState('');

  // Fetch Trip & Requests
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const tripData = await tripsApi.getSharedTrip(id);
      setTrip(tripData);
      const reqList = tripData.changeRequests || [];
      setRequests(reqList);

      // Auto-select pending requests by default
      const pendingIds = reqList.filter((r) => r.status === 'PENDING').map((r) => r.id);
      setSelectedIds(pendingIds);
    } catch (err) {
      console.error('Failed to load trip requests:', err);
      setError('Unable to load change requests. Please check your permissions or network connection.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const toggleSelect = (reqId) => {
    setSelectedIds((prev) =>
      prev.includes(reqId) ? prev.filter((i) => i !== reqId) : [...prev, reqId]
    );
  };

  const selectAllPending = () => {
    const pendingIds = requests.filter((r) => r.status === 'PENDING').map((r) => r.id);
    setSelectedIds(pendingIds);
  };

  const deselectAll = () => {
    setSelectedIds([]);
  };

  const toggleExpandDiff = (reqId) => {
    setExpandedDiffs((prev) => ({
      ...prev,
      [reqId]: !prev[reqId],
    }));
  };

  // 1. Single PR Merge Handler
  const handleMergeSingle = async (requestId) => {
    try {
      setSingleMergeLoading((prev) => ({ ...prev, [requestId]: true }));
      await tripsApi.mergeSingleChangeRequest(trip.id, requestId);
      setActionSuccess('Pull request successfully merged into trip itinerary!');
      await loadData();
    } catch (err) {
      console.error('Merge PR error:', err);
      alert(`Failed to merge PR: ${err.message || 'Unknown error'}`);
    } finally {
      setSingleMergeLoading((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  // 2. Reject PR Handlers
  const handleOpenRejectModal = (requestId) => {
    setRejectModalState({
      isOpen: true,
      requestId,
      reason: '',
      isSubmitting: false,
    });
  };

  const handleConfirmReject = async () => {
    if (!rejectModalState.requestId) return;
    try {
      setRejectModalState((prev) => ({ ...prev, isSubmitting: true }));
      await tripsApi.rejectChangeRequest(
        trip.id,
        rejectModalState.requestId,
        rejectModalState.reason
      );
      setRejectModalState({ isOpen: false, requestId: null, reason: '', isSubmitting: false });
      setActionSuccess('Change request was rejected.');
      await loadData();
    } catch (err) {
      console.error('Reject PR error:', err);
      alert(`Failed to reject PR: ${err.message || 'Unknown error'}`);
      setRejectModalState((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  // 3. Trigger Gemini AI Merge Engine
  const handleTriggerAiMerge = async () => {
    if (selectedIds.length === 0) {
      alert('Please select at least one change request to reconcile.');
      return;
    }

    const selectedRequests = requests.filter((r) => selectedIds.includes(r.id));
    const travelers = (trip.collaborators || []).map((c) => ({
      slotNumber: c.slotNumber,
      name: c.travelerInfo?.name || c.user?.fullName || 'Traveler',
      role: c.role,
      status: c.status,
      age: c.travelerInfo?.age || 28,
      gender: c.travelerInfo?.gender || 'Not Specified',
      dietary: c.travelerInfo?.dietary || 'Flexible / Any',
      preferences: c.travelerInfo?.preferences || '',
    }));

    try {
      setIsAiMerging(true);
      const response = await tripsApi.triggerAiMerge({
        originalTrip: trip,
        travelers,
        changeRequests: selectedRequests,
      });

      // Normalize response from AI service or gateway
      const mergeData = response?.mergedTrip
        ? response
        : response?.data?.mergedTrip
        ? response.data
        : response?.data || response;

      if (mergeData && (mergeData.mergedTrip || mergeData.destination || mergeData.title)) {
        setAiMergeResult(mergeData);
        setShowAiModal(true);
      } else {
        throw new Error(response?.message || 'Failed to generate AI conflict merge');
      }
    } catch (err) {
      console.error('AI Merge Error:', err);
      alert(`AI Reconciliation Error: ${err.message || 'Please verify the AI Trip Planner service is running on port 4000.'}`);
    } finally {
      setIsAiMerging(false);
    }
  };

  // 4. Apply AI Merged Plan to Database
  const handleApplyAiMergedPlan = async () => {
    const planToApply = aiMergeResult?.mergedTrip || aiMergeResult;
    if (!planToApply) return;

    try {
      setIsApplyingAi(true);
      await tripsApi.applyAiMergedPlan(trip.id, {
        requestIds: selectedIds,
        mergedPlan: planToApply,
        aiRationale: aiMergeResult.aiRationale,
      });

      setShowAiModal(false);
      setActionSuccess('AI Conflict Resolution successfully applied and merged into trip itinerary!');
      setTimeout(() => {
        navigate(`/dashboard/trip/${trip.id}`);
      }, 1500);
    } catch (err) {
      console.error('Apply AI Plan Error:', err);
      alert(`Failed to apply merged plan: ${err.message || 'Unknown error'}`);
    } finally {
      setIsApplyingAi(false);
    }
  };

  // 5. Initialize Manual Merge Modal
  const handleOpenManualMerge = () => {
    if (selectedIds.length === 0) {
      alert('Please select at least one change request to merge.');
      return;
    }
    const selectedRequests = requests.filter((r) => selectedIds.includes(r.id));
    let latestProposed = { ...trip };
    selectedRequests.forEach((r) => {
      const p = r.proposedChanges || r.proposedPlan;
      if (p) {
        latestProposed = {
          ...latestProposed,
          ...p,
        };
      }
    });

    setManualPlanDraft({
      title: latestProposed.title || trip.title,
      destination: latestProposed.destination || trip.destination,
      startDate: latestProposed.startDate || trip.startDate || '',
      endDate: latestProposed.endDate || trip.endDate || '',
      budget: latestProposed.budget || trip.budget || 50000,
      notes: `Merged ${selectedRequests.length} collaborator change request(s).`,
    });
    setShowManualModal(true);
  };

  // 6. Execute Manual Merge
  const handleExecuteManualMerge = async () => {
    try {
      setIsManualMerging(true);
      const mergedPlan = {
        title: manualPlanDraft.title,
        destination: manualPlanDraft.destination,
        startDate: manualPlanDraft.startDate,
        endDate: manualPlanDraft.endDate,
        budget: Number(manualPlanDraft.budget),
      };

      await tripsApi.manualMergeChangeRequests(trip.id, {
        requestIds: selectedIds,
        mergedPlan,
      });

      setShowManualModal(false);
      setActionSuccess('Selected change requests merged successfully!');
      setTimeout(() => {
        navigate(`/dashboard/trip/${trip.id}`);
      }, 1500);
    } catch (err) {
      console.error('Manual Merge Error:', err);
      alert(`Manual merge failed: ${err.message || 'Unknown error'}`);
    } finally {
      setIsManualMerging(false);
    }
  };

  if (loading) {
    return <Loader text="Loading Change Requests & Traveler Slots..." />;
  }

  if (error || !trip) {
    return (
      <div className="p-8 max-w-xl mx-auto text-center space-y-4">
        <AlertCircle className="w-12 h-12 text-rose-500 mx-auto" />
        <h2 className="text-xl font-bold text-gray-900">Unable to Load Requests</h2>
        <p className="text-sm text-gray-500">{error || 'Trip not found.'}</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="px-6 py-2.5 bg-[#2A9D8F] text-white font-bold rounded-xl shadow hover:bg-[#238177] cursor-pointer"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  const collaborators = trip.collaborators || [];
  const maxSlots = trip.travelers || 4;
  const pendingRequests = requests.filter((r) => r.status === 'PENDING');
  const mergedRequests = requests.filter((r) => r.status === 'MERGED');
  const rejectedRequests = requests.filter((r) => r.status === 'REJECTED');

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto space-y-8 bg-slate-50 min-h-screen">
      {/* ── Top Bar ── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <button
          onClick={() => navigate(`/dashboard/trip/${trip.id}`)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-xl border border-gray-200 text-xs font-bold transition-all shadow-xs cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Trip Itinerary</span>
        </button>

        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-amber-50 text-amber-800 border border-amber-200">
            <GitPullRequest className="w-3.5 h-3.5 text-amber-600" />
            <span>{pendingRequests.length} Pending PRs</span>
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            <span>{mergedRequests.length} Merged</span>
          </span>
          {rejectedRequests.length > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-rose-50 text-rose-800 border border-rose-200">
              <XCircle className="w-3.5 h-3.5 text-rose-600" />
              <span>{rejectedRequests.length} Rejected</span>
            </span>
          )}
        </div>
      </div>

      {/* Success Notification Alert */}
      {actionSuccess && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center justify-between gap-3 text-emerald-800 font-semibold animate-fade-in shadow-xs">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            <span>{actionSuccess}</span>
          </div>
          <button
            onClick={() => setActionSuccess('')}
            className="text-emerald-600 hover:text-emerald-900 text-xs font-bold underline cursor-pointer"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* ── Header Banner ── */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <span className="text-xs font-bold text-[#2A9D8F] uppercase tracking-wider">
              Collaborative Trip Reconciler
            </span>
            <h1 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight mt-1">
              Pull Requests &amp; Conflict Merger
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Review companion proposals, inspect visual Git-style diffs, and use Gemini AI or manual merging to harmonize conflicting budgets, dates, and dietary preferences.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleTriggerAiMerge}
              disabled={isAiMerging || selectedIds.length === 0}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white rounded-xl text-xs font-black shadow-md transition-all cursor-pointer disabled:opacity-50"
            >
              {isAiMerging ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              <span>{isAiMerging ? 'Reconciling via Gemini AI...' : `AI Merge Selected (${selectedIds.length})`}</span>
            </button>

            <button
              onClick={handleOpenManualMerge}
              disabled={selectedIds.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 hover:bg-black text-white rounded-xl text-xs font-bold shadow-sm transition-all cursor-pointer disabled:opacity-50"
            >
              <Split className="w-4 h-4" />
              <span>Manual Merge ({selectedIds.length})</span>
            </button>
          </div>
        </div>

        {/* Baseline Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-gray-100 text-xs">
          <div className="p-3 bg-gray-50 rounded-xl">
            <span className="text-gray-400 block font-medium">Destination</span>
            <span className="font-bold text-gray-900 flex items-center gap-1 mt-0.5">
              <MapPin className="w-3.5 h-3.5 text-[#2A9D8F]" />
              <span>{trip.destination}</span>
            </span>
          </div>
          <div className="p-3 bg-gray-50 rounded-xl">
            <span className="text-gray-400 block font-medium">Dates &amp; Duration</span>
            <span className="font-bold text-gray-900 flex items-center gap-1 mt-0.5">
              <Calendar className="w-3.5 h-3.5 text-[#2A9D8F]" />
              <span>{trip.startDate || 'TBD'} ({trip.totalDays || trip.days?.length || 3}d)</span>
            </span>
          </div>
          <div className="p-3 bg-gray-50 rounded-xl">
            <span className="text-gray-400 block font-medium">Baseline Budget</span>
            <span className="font-bold text-gray-900 flex items-center gap-1 mt-0.5">
              <Wallet className="w-3.5 h-3.5 text-[#2A9D8F]" />
              <span>₹{Number(trip.budget || 0).toLocaleString()}</span>
            </span>
          </div>
          <div className="p-3 bg-gray-50 rounded-xl">
            <span className="text-gray-400 block font-medium">Traveler Capacity</span>
            <span className="font-bold text-gray-900 flex items-center gap-1 mt-0.5">
              <Users className="w-3.5 h-3.5 text-[#2A9D8F]" />
              <span>{collaborators.length} of {maxSlots} Filled</span>
            </span>
          </div>
        </div>
      </div>

      {/* ── Traveler Slots Breakdown ── */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-black text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#2A9D8F]" />
            <span>Traveler Slots &amp; Companion Roster</span>
          </h2>
          <span className="text-xs font-semibold text-gray-500">
            Slot 1 is Organizer
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: Math.max(maxSlots, collaborators.length) }).map((_, idx) => {
            const slotNum = idx + 1;
            const collab = collaborators.find((c) => c.slotNumber === slotNum);
            const isOrganizer = slotNum === 1;

            if (collab) {
              const info = collab.travelerInfo || {};
              const travelerName = info.name || collab.user?.fullName || (isOrganizer ? 'Trip Organizer' : `Traveler ${slotNum}`);
              const isAccepted = collab.status === 'ACCEPTED';

              return (
                <div
                  key={slotNum}
                  className={`p-4 rounded-2xl border transition-all ${
                    isOrganizer
                      ? 'bg-teal-50/50 border-teal-200'
                      : isAccepted
                      ? 'bg-emerald-50/40 border-emerald-200'
                      : 'bg-amber-50/40 border-amber-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-white border text-gray-700">
                      Slot #{slotNum}
                    </span>
                    {isOrganizer ? (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#2A9D8F] text-white">
                        Organizer
                      </span>
                    ) : isAccepted ? (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white flex items-center gap-1">
                        <Check className="w-2.5 h-2.5" /> Accepted
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-white">
                        Pending
                      </span>
                    )}
                  </div>

                  <div className="mt-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gray-200 overflow-hidden shrink-0">
                      <img
                        src={collab.user?.avatarUrl || `https://api.dicebear.com/7.x/bottts/svg?seed=slot-${slotNum}`}
                        alt={travelerName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="overflow-hidden">
                      <h4 className="text-sm font-bold text-gray-900 truncate">{travelerName}</h4>
                      <p className="text-[11px] text-gray-500 truncate">{collab.user?.email || 'Registered Traveler'}</p>
                    </div>
                  </div>

                  <div className="mt-3 pt-2 border-t border-gray-100 grid grid-cols-2 gap-1 text-[11px] text-gray-600">
                    <div>
                      <span className="text-gray-400 block text-[10px]">Age / Gender</span>
                      <span className="font-semibold">{info.age || 28} y/o • {info.gender || 'Any'}</span>
                    </div>
                    <div>
                      <span className="text-gray-400 block text-[10px]">Dietary</span>
                      <span className="font-semibold truncate block">{info.dietary || 'Flexible'}</span>
                    </div>
                  </div>
                </div>
              );
            }

            // Vacant Slot
            return (
              <div
                key={slotNum}
                className="p-4 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50/50 flex flex-col items-center justify-center text-center space-y-1.5 min-h-[140px]"
              >
                <div className="w-8 h-8 rounded-full bg-gray-200/60 flex items-center justify-center text-gray-400">
                  <User className="w-4 h-4" />
                </div>
                <span className="text-xs font-bold text-gray-700">Slot #{slotNum}: Open Spot</span>
                <p className="text-[11px] text-gray-400 max-w-[180px]">
                  Waiting for friend to accept trip invitation.
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Change Requests List (GitHub PR Workflow) ── */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-4">
          <div>
            <h2 className="text-lg font-black text-gray-900 flex items-center gap-2">
              <GitPullRequest className="w-5 h-5 text-amber-500" />
              <span>Companion Change Requests ({requests.length})</span>
            </h2>
            <p className="text-xs text-gray-500">
              Select requests for AI/Manual merging, or review, accept, or reject each PR individually.
            </p>
          </div>

          <div className="flex items-center gap-2 text-xs">
            <button
              onClick={selectAllPending}
              className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 font-semibold text-gray-700 cursor-pointer"
            >
              Select All Pending
            </button>
            <button
              onClick={deselectAll}
              className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 font-semibold text-gray-500 cursor-pointer"
            >
              Clear Selection
            </button>
          </div>
        </div>

        {requests.length === 0 ? (
          <div className="p-8 text-center space-y-2 text-gray-400">
            <GitPullRequest className="w-10 h-10 mx-auto text-gray-300" />
            <p className="text-sm font-semibold text-gray-600">No Change Requests Yet</p>
            <p className="text-xs">
              When companions propose changes to dates, budget, or stays, their requests will appear here for review and merge.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((req) => {
              const isSelected = selectedIds.includes(req.id);
              const isPending = req.status === 'PENDING';
              const isMerged = req.status === 'MERGED';
              const isRejected = req.status === 'REJECTED';
              const isExpanded = !!expandedDiffs[req.id];
              const proposed = req.proposedChanges || req.proposedPlan || {};
              const isMergingThis = !!singleMergeLoading[req.id];

              // Calculate Budget Delta
              const baselineBudget = Number(trip.budget || 0);
              const proposedBudget = Number(proposed.budget || baselineBudget);
              const budgetDelta = proposedBudget - baselineBudget;

              return (
                <div
                  key={req.id}
                  className={`rounded-2xl border transition-all ${
                    isSelected
                      ? 'border-teal-400 bg-teal-50/20 shadow-sm'
                      : isMerged
                      ? 'border-emerald-200 bg-emerald-50/10'
                      : isRejected
                      ? 'border-rose-200 bg-rose-50/10'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  {/* Card Header */}
                  <div className="p-4 sm:p-5 flex flex-wrap items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      {isPending && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(req.id)}
                          className="mt-1 w-4 h-4 text-[#2A9D8F] rounded border-gray-300 focus:ring-[#2A9D8F] cursor-pointer"
                        />
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md ${
                              isMerged
                                ? 'bg-emerald-100 text-emerald-800'
                                : isPending
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-rose-100 text-rose-800'
                            }`}
                          >
                            {req.status}
                          </span>
                          <span className="text-xs text-gray-400">
                            {new Date(req.createdAt).toLocaleDateString()} at{' '}
                            {new Date(req.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>

                        <h3 className="text-sm sm:text-base font-bold text-gray-900 mt-1">
                          {req.title || req.note || 'Proposed itinerary updates'}
                        </h3>

                        {req.notes && req.notes !== req.title && (
                          <p className="text-xs text-gray-600 mt-0.5 bg-gray-50 px-2 py-1 rounded-lg border border-gray-100">
                            💬 "{req.notes}"
                          </p>
                        )}

                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                          <img
                            src={req.user?.avatarUrl || `https://api.dicebear.com/7.x/bottts/svg?seed=${req.userId}`}
                            alt={req.user?.fullName || 'Traveler'}
                            className="w-4 h-4 rounded-full"
                          />
                          <span className="font-semibold text-gray-700">
                            {req.user?.fullName || 'Traveler'}
                          </span>
                          <span>({req.user?.email || 'Companion'})</span>
                        </div>
                      </div>
                    </div>

                    {/* PR Actions & Visual Diff Toggle */}
                    <div className="flex flex-wrap items-center gap-2">
                      {isPending && (
                        <>
                          <button
                            onClick={() => handleMergeSingle(req.id)}
                            disabled={isMergingThis}
                            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all cursor-pointer disabled:opacity-50"
                          >
                            {isMergingThis ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <GitMerge className="w-3.5 h-3.5" />
                            )}
                            <span>{isMergingThis ? 'Merging...' : 'Merge this PR'}</span>
                          </button>

                          <button
                            onClick={() => handleOpenRejectModal(req.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 text-xs font-bold rounded-xl transition-all cursor-pointer"
                          >
                            <XCircle className="w-3.5 h-3.5" />
                            <span>Reject PR</span>
                          </button>
                        </>
                      )}

                      {isMerged && (
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-xl border border-emerald-200">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span>Merged</span>
                        </span>
                      )}

                      {isRejected && (
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-rose-50 text-rose-700 text-xs font-bold rounded-xl border border-rose-200">
                          <XCircle className="w-3.5 h-3.5" />
                          <span>Rejected</span>
                        </span>
                      )}

                      <button
                        onClick={() => toggleExpandDiff(req.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-gray-200 hover:bg-gray-50 text-xs font-semibold text-gray-700 cursor-pointer"
                      >
                        <span>{isExpanded ? 'Hide Visual Diff' : 'View Visual Diff'}</span>
                        {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>

                  {/* Expandable Git-Style Visual Diff View */}
                  {isExpanded && (
                    <div className="p-4 sm:p-6 bg-slate-50 border-t border-gray-100 rounded-b-2xl space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                          <Split className="w-3.5 h-3.5 text-teal-600" />
                          <span>Visual Git-Style Diff: Baseline Trip vs. Companion Proposal</span>
                        </span>
                        {budgetDelta !== 0 && (
                          <span
                            className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                              budgetDelta > 0
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-emerald-100 text-emerald-800'
                            }`}
                          >
                            Budget Delta: {budgetDelta > 0 ? `+₹${budgetDelta.toLocaleString()}` : `-₹${Math.abs(budgetDelta).toLocaleString()}`}
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                        {/* Baseline Box */}
                        <div className="p-4 rounded-xl bg-white border border-gray-200 space-y-2.5">
                          <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                            <span className="font-bold text-gray-500 uppercase text-[10px]">
                              Current Baseline Trip
                            </span>
                            <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded font-semibold">
                              Original
                            </span>
                          </div>

                          <div className="space-y-1.5">
                            <div className="flex items-start justify-between">
                              <span className="text-gray-500">Destination:</span>
                              <span className="font-bold text-gray-900">{trip.destination}</span>
                            </div>
                            <div className="flex items-start justify-between">
                              <span className="text-gray-500">Dates:</span>
                              <span className="font-bold text-gray-900">{trip.startDate || 'TBD'} to {trip.endDate || 'TBD'}</span>
                            </div>
                            <div className="flex items-start justify-between">
                              <span className="text-gray-500">Duration:</span>
                              <span className="font-bold text-gray-900">{trip.totalDays || trip.days?.length || 3} Days</span>
                            </div>
                            <div className="flex items-start justify-between">
                              <span className="text-gray-500">Budget:</span>
                              <span className="font-bold text-gray-900">₹{baselineBudget.toLocaleString()}</span>
                            </div>
                          </div>
                        </div>

                        {/* Proposed Box */}
                        <div className="p-4 rounded-xl bg-teal-50/60 border border-teal-200 space-y-2.5">
                          <div className="flex items-center justify-between border-b border-teal-100 pb-2">
                            <span className="font-bold text-teal-800 uppercase text-[10px]">
                              Proposed by {req.user?.fullName || 'Companion'}
                            </span>
                            <span className="text-[10px] px-2 py-0.5 bg-teal-200 text-teal-900 rounded font-bold">
                              Modified
                            </span>
                          </div>

                          <div className="space-y-1.5">
                            <div className="flex items-start justify-between">
                              <span className="text-teal-900">Destination:</span>
                              <span className={`font-bold ${proposed.destination && proposed.destination !== trip.destination ? 'bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded' : 'text-gray-900'}`}>
                                {proposed.destination || trip.destination}
                              </span>
                            </div>
                            <div className="flex items-start justify-between">
                              <span className="text-teal-900">Dates:</span>
                              <span className={`font-bold ${proposed.startDate && proposed.startDate !== trip.startDate ? 'bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded' : 'text-gray-900'}`}>
                                {proposed.startDate || trip.startDate || 'TBD'} to {proposed.endDate || trip.endDate || 'TBD'}
                              </span>
                            </div>
                            <div className="flex items-start justify-between">
                              <span className="text-teal-900">Duration:</span>
                              <span className="font-bold text-gray-900">
                                {proposed.days?.length || proposed.totalDays || trip.totalDays || trip.days?.length || 3} Days
                              </span>
                            </div>
                            <div className="flex items-start justify-between">
                              <span className="text-teal-900">Budget:</span>
                              <span className={`font-bold ${budgetDelta !== 0 ? 'bg-emerald-100 text-emerald-900 px-1.5 py-0.5 rounded' : 'text-gray-900'}`}>
                                ₹{proposedBudget.toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Companion Requirements / Dietary / Specific Notes */}
                      {proposed.travelerRequirements && (
                        <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 text-xs space-y-1">
                          <span className="font-bold text-amber-900 block flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                            Companion Traveler Requirements / Preferences:
                          </span>
                          <p className="text-amber-800 leading-relaxed font-medium">
                            {proposed.travelerRequirements}
                          </p>
                        </div>
                      )}

                      {/* Day-by-Day Diff Highlights (if present in proposedChanges) */}
                      {Array.isArray(proposed.days) && proposed.days.length > 0 && (
                        <div className="space-y-2 pt-2 border-t border-gray-200">
                          <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wider block">
                            Proposed Itinerary Day Changes ({proposed.days.length} Days)
                          </span>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {proposed.days.map((d, dIdx) => (
                              <div key={dIdx} className="p-2.5 bg-white rounded-lg border border-gray-200 text-[11px] space-y-1">
                                <span className="font-bold text-teal-700 block">
                                  Day {d.dayNumber || dIdx + 1}: {d.theme || d.title || 'Exploration'}
                                </span>
                                {d.activities && (
                                  <p className="text-gray-600 truncate">
                                    🎯 {Array.isArray(d.activities) ? d.activities.map(a => a.name || a.activity || a).join(', ') : d.activities}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Reject PR Reason Modal ── */}
      {rejectModalState.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 space-y-5 shadow-2xl border border-gray-100">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-black text-gray-900 flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-rose-500" />
                  <span>Reject Change Request</span>
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Optionally provide a reason so your companion understands why their proposed changes were declined.
                </p>
              </div>
              <button
                onClick={() => setRejectModalState({ isOpen: false, requestId: null, reason: '', isSubmitting: false })}
                className="p-1 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-700 block mb-1">
                Reason for Rejection (Optional)
              </label>
              <textarea
                rows={3}
                value={rejectModalState.reason}
                onChange={(e) => setRejectModalState({ ...rejectModalState, reason: e.target.value })}
                placeholder="e.g., Dates clash with organizer availability, or budget exceeds group constraints."
                className="w-full text-xs p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-400 outline-none"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setRejectModalState({ isOpen: false, requestId: null, reason: '', isSubmitting: false })}
                className="px-4 py-2 text-xs font-bold text-gray-600 hover:bg-gray-100 rounded-xl cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmReject}
                disabled={rejectModalState.isSubmitting}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl shadow-xs cursor-pointer disabled:opacity-50"
              >
                {rejectModalState.isSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                <span>{rejectModalState.isSubmitting ? 'Rejecting...' : 'Confirm Rejection'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── AI Rationale & Reconciliation Modal ── */}
      {showAiModal && aiMergeResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100 p-6 sm:p-8 space-y-6">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 pb-4">
              <div>
                <span className="text-xs font-black text-teal-600 uppercase tracking-wider flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4" />
                  <span>Gemini AI Conflict Reconciliation</span>
                </span>
                <h3 className="text-xl sm:text-2xl font-black text-gray-900 mt-1">
                  Reconciled Trip Plan &amp; Decision Rationale
                </h3>
              </div>
              <button
                onClick={() => setShowAiModal(false)}
                className="p-1.5 rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            {/* AI Decision Rationale Breakdown */}
            {aiMergeResult.aiRationale && (
              <div className="bg-teal-50/50 rounded-2xl p-5 border border-teal-200 space-y-4 text-xs">
                <h4 className="text-sm font-black text-teal-900 flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-teal-600" />
                  <span>AI Conflict Resolution Rationale</span>
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-gray-700">
                  {aiMergeResult.aiRationale.demographicHarmonization && (
                    <div className="p-3 bg-white rounded-xl border border-teal-100 space-y-1">
                      <span className="font-bold text-gray-900 flex items-center gap-1">
                        <Users className="w-3.5 h-3.5 text-teal-600" />
                        <span>Demographics &amp; Pacing</span>
                      </span>
                      <p className="text-[11px] leading-relaxed">
                        {aiMergeResult.aiRationale.demographicHarmonization}
                      </p>
                    </div>
                  )}

                  {aiMergeResult.aiRationale.dietaryAccommodation && (
                    <div className="p-3 bg-white rounded-xl border border-teal-100 space-y-1">
                      <span className="font-bold text-gray-900 flex items-center gap-1">
                        <Utensils className="w-3.5 h-3.5 text-amber-500" />
                        <span>Dietary &amp; Dining</span>
                      </span>
                      <p className="text-[11px] leading-relaxed">
                        {aiMergeResult.aiRationale.dietaryAccommodation}
                      </p>
                    </div>
                  )}

                  {aiMergeResult.aiRationale.weatherOptimization && (
                    <div className="p-3 bg-white rounded-xl border border-teal-100 space-y-1">
                      <span className="font-bold text-gray-900 flex items-center gap-1">
                        <SunMedium className="w-3.5 h-3.5 text-amber-600" />
                        <span>Climate &amp; Weather Adaptation</span>
                      </span>
                      <p className="text-[11px] leading-relaxed">
                        {aiMergeResult.aiRationale.weatherOptimization}
                      </p>
                    </div>
                  )}

                  {aiMergeResult.aiRationale.budgetReconciliation && (
                    <div className="p-3 bg-white rounded-xl border border-teal-100 space-y-1">
                      <span className="font-bold text-gray-900 flex items-center gap-1">
                        <Wallet className="w-3.5 h-3.5 text-emerald-600" />
                        <span>Budget Reconciliation</span>
                      </span>
                      <p className="text-[11px] leading-relaxed">
                        {aiMergeResult.aiRationale.budgetReconciliation}
                      </p>
                    </div>
                  )}
                </div>

                {aiMergeResult.aiRationale.summary && (
                  <p className="text-xs font-medium text-teal-900 italic pt-1 border-t border-teal-100">
                    "{aiMergeResult.aiRationale.summary}"
                  </p>
                )}
              </div>
            )}

            {/* Reconciled Plan Overview */}
            <div className="space-y-3">
              <span className="text-xs font-bold text-gray-700 uppercase tracking-wider block">
                Reconciled Trip Specifications
              </span>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                  <span className="text-gray-400 block">Title</span>
                  <span className="font-bold text-gray-900 truncate block">
                    {aiMergeResult.mergedTrip?.title || aiMergeResult.title || trip.title}
                  </span>
                </div>
                <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                  <span className="text-gray-400 block">Reconciled Budget</span>
                  <span className="font-bold text-emerald-600">
                    ₹{Number(aiMergeResult.mergedTrip?.budget || aiMergeResult.budget || trip.budget).toLocaleString()}
                  </span>
                </div>
                <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                  <span className="text-gray-400 block">Planned Days</span>
                  <span className="font-bold text-gray-900">
                    {aiMergeResult.mergedTrip?.days?.length || aiMergeResult.days?.length || trip.totalDays} Days
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex flex-wrap items-center justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                onClick={() => setShowAiModal(false)}
                className="px-4 py-2 text-xs font-bold text-gray-600 hover:bg-gray-100 rounded-xl cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleApplyAiMergedPlan}
                disabled={isApplyingAi}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#2A9D8F] hover:bg-[#238177] text-white rounded-xl text-xs font-black shadow-md cursor-pointer disabled:opacity-50"
              >
                {isApplyingAi ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                <span>{isApplyingAi ? 'Applying to Database...' : 'Apply AI Merged Plan to Trip'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Manual Merge Modal ── */}
      {showManualModal && manualPlanDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl max-w-xl w-full p-6 sm:p-8 space-y-6 shadow-2xl border border-gray-100">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 pb-3">
              <div>
                <h3 className="text-lg font-black text-gray-900">Manual Merge Configuration</h3>
                <p className="text-xs text-gray-500">
                  Review and confirm the new baseline values before committing.
                </p>
              </div>
              <button
                onClick={() => setShowManualModal(false)}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-gray-700 block mb-1">Trip Title</label>
                <input
                  type="text"
                  value={manualPlanDraft.title}
                  onChange={(e) => setManualPlanDraft({ ...manualPlanDraft, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-[#2A9D8F] outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-gray-700 block mb-1">Start Date</label>
                  <input
                    type="date"
                    value={manualPlanDraft.startDate}
                    onChange={(e) => setManualPlanDraft({ ...manualPlanDraft, startDate: e.target.value })}
                    className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-[#2A9D8F] outline-none"
                  />
                </div>
                <div>
                  <label className="font-bold text-gray-700 block mb-1">End Date</label>
                  <input
                    type="date"
                    value={manualPlanDraft.endDate}
                    onChange={(e) => setManualPlanDraft({ ...manualPlanDraft, endDate: e.target.value })}
                    className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-[#2A9D8F] outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="font-bold text-gray-700 block mb-1">Total Budget (₹)</label>
                <input
                  type="number"
                  value={manualPlanDraft.budget}
                  onChange={(e) => setManualPlanDraft({ ...manualPlanDraft, budget: e.target.value })}
                  className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-[#2A9D8F] outline-none font-mono"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
              <button
                onClick={() => setShowManualModal(false)}
                className="px-4 py-2 text-xs font-bold text-gray-600 hover:bg-gray-100 rounded-xl cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleExecuteManualMerge}
                disabled={isManualMerging}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-slate-900 hover:bg-black text-white rounded-xl text-xs font-bold shadow cursor-pointer disabled:opacity-50"
              >
                {isManualMerging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                <span>{isManualMerging ? 'Merging...' : 'Confirm & Merge'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
